import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  TextFields,
  Image,
  SmartButton as ButtonIcon,
  ViewColumn,
  Article,
  List as ListIcon,
  Link,
  SpaceDashboard,
  Email,
  Person,
  Business,
  Event,
  Star,
  Search,
  DragIndicator,
} from '@mui/icons-material';

const BlockLibrary = ({ onAddBlock }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('basic');

  const blockCategories = [
    {
      id: 'basic',
      name: 'Basic Elements',
      icon: <SpaceDashboard />,
      blocks: [
        {
          type: 'text',
          name: 'Text Block',
          icon: <TextFields />,
          description: 'Add text content',
          defaultData: {
            content: 'Your text here...',
            fontSize: 16,
            color: '#333333',
            fontWeight: 'normal',
            fontStyle: 'normal',
            textDecoration: 'none',
            textAlign: 'left',
            lineHeight: 1.5,
            fontFamily: 'Arial, sans-serif',
            marginTop: 0,
            marginBottom: 15,
            padding: 10,
          }
        },
        {
          type: 'heading',
          name: 'Heading',
          icon: <Article />,
          description: 'Add a heading',
          defaultData: {
            content: 'Your Heading',
            level: 'h2',
            fontSize: 24,
            color: '#333333',
            fontWeight: 'bold',
            fontStyle: 'normal',
            textDecoration: 'none',
            textAlign: 'left',
            lineHeight: 1.2,
            marginTop: 20,
            marginBottom: 15,
            padding: 10,
            fontFamily: 'Arial, sans-serif',
          }
        },
        {
          type: 'image',
          name: 'Image',
          icon: <Image />,
          description: 'Add an image',
          defaultData: {
            src: '',
            alt: 'Image description',
            width: '100%',
            height: 'auto',
            alignment: 'center',
            borderRadius: 0,
            link: '',
          }
        },
        {
          type: 'button',
          name: 'Button',
          icon: <ButtonIcon />,
          description: 'Call-to-action button',
          defaultData: {
            text: 'Click Here',
            link: '#',
            backgroundColor: '#007bff',
            color: '#ffffff',
            borderRadius: 5,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            alignment: 'center',
            fullWidth: false,
          }
        },
        {
          type: 'divider',
          name: 'Divider',
          icon: <ViewColumn />,
          description: 'Horizontal line separator',
          defaultData: {
            height: 1,
            color: '#dddddd',
            backgroundColor: 'transparent',
            style: 'solid',
            width: '100%',
            marginTop: 20,
            marginBottom: 20,
            padding: 0,
            borderRadius: 0,
            opacity: 1,
            alignment: 'center',
          }
        },
        {
          type: 'spacer',
          name: 'Spacer',
          icon: <SpaceDashboard />,
          description: 'Add vertical space',
          defaultData: {
            height: 30,
            backgroundColor: 'transparent',
          }
        },
      ]
    },
    {
      id: 'layout',
      name: 'Layout Elements',
      icon: <ViewColumn />,
      blocks: [
        {
          type: 'columns',
          name: '2 Columns',
          icon: <ViewColumn />,
          description: 'Two column layout',
          defaultData: {
            columns: 2,
            columnGap: 20,
            leftWidth: '50%',
            rightWidth: '50%',
            verticalAlign: 'top',
            leftContent: [],
            rightContent: [],
          }
        },
        {
          type: 'columns-3',
          name: '3 Columns',
          icon: <ViewColumn />,
          description: 'Three column layout',
          defaultData: {
            columns: 3,
            columnGap: 15,
            columnWidths: ['33.33%', '33.33%', '33.33%'],
            verticalAlign: 'top',
            content: [[], [], []],
          }
        },
        {
          type: 'container',
          name: 'Container',
          icon: <SpaceDashboard />,
          description: 'Single column container',
          defaultData: {
            backgroundColor: 'transparent',
            padding: 10,
            borderRadius: 0,
            border: 'none',
            maxWidth: '100%',
            content: [],
            marginTop: 15,
            marginBottom: 15,
            verticalAlign: 'top',
          }
        },
      ]
    },
    {
      id: 'content',
      name: 'Content Blocks',
      icon: <Article />,
      blocks: [
        {
          type: 'hero',
          name: 'Hero Section',
          icon: <Star />,
          description: 'Large banner section',
          defaultData: {
            title: 'Welcome to Our Newsletter',
            subtitle: 'Stay updated with our latest news',
            backgroundImage: '',
            backgroundColor: '#f8f9fa',
            titleColor: '#333333',
            subtitleColor: '#666666',
            buttonBackgroundColor: '#007bff',
            buttonTextColor: '#ffffff',
            textAlign: 'center',
            padding: '60px 20px',
            fontFamily: 'Arial, sans-serif',
            buttonText: 'Get Started',
            buttonLink: '#',
            showButton: true,
          }
        },
        {
          type: 'list',
          name: 'List',
          icon: <ListIcon />,
          description: 'Bullet or numbered list',
          defaultData: {
            items: ['Item 1', 'Item 2', 'Item 3'],
            listType: 'bullet',
            fontSize: 16,
            color: '#333333',
            lineHeight: 1.6,
            spacing: 8,
            fontFamily: 'Arial, sans-serif',
          }
        },
        {
          type: 'testimonial',
          name: 'Testimonial',
          icon: <Person />,
          description: 'Customer testimonial',
          defaultData: {
            quote: 'This is an amazing product that has helped our business grow!',
            author: 'John Doe',
            position: 'CEO',
            companyName: 'Company Name',
            authorImage: '',
            backgroundColor: '#f8f9fa',
            textColor: '#333333',
            quoteColor: '#333333',
            authorColor: '#666666',
            positionColor: '#999999',
            companyColor: '#999999',
            quoteFontSize: 16,
            authorFontSize: 14,
            borderRadius: 8,
            alignment: 'center',
            fontFamily: 'Arial, sans-serif',
            marginTop: 20,
            marginBottom: 20,
            padding: 30,
          }
        },
        {
          type: 'product',
          name: 'Product Showcase',
          icon: <Business />,
          description: 'Display product information',
          defaultData: {
            image: '',
            name: 'Product Name',
            description: 'Product description goes here...',
            price: '$99.99',
            originalPrice: '',
            buttonText: 'Buy Now',
            buttonLink: '#',
            alignment: 'left',
            titleColor: '#333333',
            nameColor: '#333333',
            descriptionColor: '#666666',
            priceColor: '#e74c3c',
            buttonBackgroundColor: '#007bff',
            backgroundColor: 'transparent',
            marginTop: 20,
            marginBottom: 20,
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
          }
        },
        {
          type: 'event',
          name: 'Event Info',
          icon: <Event />,
          description: 'Event details block',
          defaultData: {
            title: 'Event Title',
            date: new Date().toISOString().split('T')[0],
            time: '19:00',
            location: 'Event Location',
            description: 'Event description...',
            registerLink: '#',
            buttonText: 'Register Now',
            buttonLink: '#',
            backgroundColor: '#ffffff',
            titleColor: '#333333',
            descriptionColor: '#555555',
            dateColor: '#666666',
            buttonBackgroundColor: '#007bff',
            marginTop: 20,
            marginBottom: 20,
            padding: '25px',
            fontFamily: 'Arial, sans-serif',
          }
        },
      ]
    },
    {
      id: 'social',
      name: 'Social & Links',
      icon: <Link />,
      blocks: [
        {
          type: 'social-icons',
          name: 'Social Icons',
          icon: <Link />,
          description: 'Social media & messenger links',
          defaultData: {
            platforms: [
              { category: 'social', name: 'Facebook', url: '#', show: true },
              { category: 'social', name: 'Twitter', url: '#', show: true },
              { category: 'social', name: 'Instagram', url: '#', show: true },
              { category: 'messenger', name: 'Telegram', url: '#', show: true },
            ],
            iconSize: 32,
            spacing: 10,
            alignment: 'center',
            style: 'colored',
            borderRadius: 50,
            backgroundColor: '#666666',
            iconColor: '#ffffff',
          }
        },
        {
          type: 'footer',
          name: 'Footer',
          icon: <Email />,
          description: 'Email footer section',
          defaultData: {
            companyName: 'Your Company',
            address: '123 Main St, City, State 12345',
            phone: '',
            email: '',
            website: '',
            backgroundColor: '#f8f9fa',
            textColor: '#666666',
            fontSize: 12,
            padding: '30px 20px',
            fontFamily: 'Arial, sans-serif',
          }
        },
        {
          type: 'contactUs',
          name: 'Contact Us',
          icon: <Person />,
          description: 'Contact cards section',
          defaultData: {
            title: 'Contact Us',
            contacts: [
              {
                id: '1',
                avatar: '',
                fullName: 'John Doe',
                position: 'CEO',
                description: 'Feel free to reach out for any business inquiries.',
                phone: '+1 (555) 123-4567',
                email: 'john@company.com'
              }
            ],
            backgroundColor: '#ffffff',
            titleColor: '#333333',
            nameColor: '#333333',
            positionColor: '#666666',
            descriptionColor: '#666666',
            contactColor: '#007bff',
            borderColor: '#e0e0e0',
            titleFontSize: 28,
            nameFontSize: 18,
            positionFontSize: 14,
            descriptionFontSize: 14,
            contactFontSize: 14,
            alignment: 'center',
            cardsPerRow: 2,
            cardSpacing: 20,
            padding: 30,
            marginTop: 20,
            marginBottom: 20,
            borderRadius: 8,
            fontFamily: 'Arial, sans-serif',
          }
        },
      ]
    },
  ];

  const filteredCategories = blockCategories.map(category => ({
    ...category,
    blocks: category.blocks.filter(block =>
      block.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      block.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.blocks.length > 0);

  const handleDragStart = (e, block) => {
    const blockData = {
      type: block.type,
      defaultData: block.defaultData,
      fromCanvas: false,
      fromLibrary: true,
    };
    e.dataTransfer.setData('application/json', JSON.stringify(blockData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleAddBlock = (block) => {
    onAddBlock(block.type, block.defaultData);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Заголовок */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 'bold' }}>
          Block Library
        </Typography>
        
        {/* Поиск */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search blocks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Список блоков */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredCategories.map((category) => (
          <Accordion
            key={category.id}
            expanded={expandedCategory === category.id}
            onChange={() => setExpandedCategory(
              expandedCategory === category.id ? '' : category.id
            )}
            sx={{ 
              boxShadow: 'none',
              '&:before': { display: 'none' },
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#e9ecef' },
                minHeight: 48,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 1,
                }
              }}
            >
              {category.icon}
              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                {category.name}
              </Typography>
              <Typography variant="caption" sx={{ ml: 'auto', color: '#666' }}>
                {category.blocks.length}
              </Typography>
            </AccordionSummary>
            
            <AccordionDetails sx={{ p: 0 }}>
              <List sx={{ p: 0 }}>
                {category.blocks.map((block, index) => (
                  <ListItem
                    key={`${block.type}-${index}`}
                    sx={{
                      p: 0,
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'pointer',
                    }}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        boxShadow: 'none',
                        border: '1px solid transparent',
                        '&:hover': {
                          border: '1px solid #007bff',
                          backgroundColor: '#f8f9ff',
                        }
                      }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, block)}
                      onClick={() => handleAddBlock(block)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box sx={{ 
                            color: '#007bff', 
                            display: 'flex', 
                            alignItems: 'center',
                            minWidth: 24,
                          }}>
                            {block.icon}
                          </Box>
                          
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 'medium',
                                lineHeight: 1.3,
                                mb: 0.5,
                              }}
                            >
                              {block.name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                display: 'block',
                                lineHeight: 1.3,
                              }}
                            >
                              {block.description}
                            </Typography>
                          </Box>
                          
                          <DragIndicator 
                            sx={{ 
                              color: '#bbb', 
                              fontSize: 16,
                              opacity: 0.5,
                            }} 
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Подсказка */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          💡 Tip: Click to add or drag blocks to the canvas
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Use the search above to quickly find blocks
        </Typography>
      </Box>
    </Box>
  );
};

export default BlockLibrary; 